import React, { useMemo } from 'react';
import { Box, Button, Flex, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { LockIcon } from '@chakra-ui/icons';
import type { WalletPendingBreakdown, WalletPendingCategory } from '@foodfluence/shared';
import {
  EMPTY_WALLET_PENDING_BREAKDOWN,
  getPendingCategoryLabel,
  getPendingCategoryTooltip,
} from '@foodfluence/shared';
import { formatWalletBalance } from '../utils/walletUtils';
import { ff } from '../../../theme';
import { MoneyFigure } from '../../../components/ui';
import { walletColors } from '../walletTheme';
import WalletHeroChrome from './WalletHeroChrome';
import { WalletOutlineIcon, WalletWithdrawArrowIcon } from './WalletHeroIcons';

const PENDING_CATEGORY_ORDER: WalletPendingCategory[] = [
  'awaitingBrandPayment',
  'readyToTransfer',
  'awaitingWhopSetup',
  'transferError',
];

export interface WalletBalanceHeroProps {
  /** USD available to withdraw from Whop account (via POST /wallet/bootstrap). */
  availableBalance?: number;
  balanceLoading?: boolean;
  pendingBreakdown?: WalletPendingBreakdown;
  /** Creator has a Whop company (balance may still load). */
  hasWhopAccount?: boolean;
  /** Whop Elements active — enables payout action slot. */
  withdrawEnabled?: boolean;
  connecting?: boolean;
  onConnect?: () => void;
  /** Profile preview: tap card body (not payout action) to open full wallet. */
  onOpenWallet?: () => void;
  loading?: boolean;
  /** Embedded Verify yourself / Withdraw control (WalletPayoutsButton). */
  payoutAction?: React.ReactNode;
}

const PendingCategoryRow: React.FC<{ category: WalletPendingCategory; amount: number }> = ({
  category,
  amount,
}) => {
  const formattedAmount = formatWalletBalance(amount);
  const label =
    category === 'awaitingBrandPayment' ? 'Pending' : getPendingCategoryLabel(category);

  return (
    <Tooltip
      label={getPendingCategoryTooltip(category, formattedAmount)}
      placement="top"
      hasArrow
      bg={ff.inkSoft}
      color="white"
      fontSize="12px"
      px={3}
      py={2}
      borderRadius="8px"
      maxW="240px"
    >
      <Flex align="center" gap="6px" cursor="pointer">
        <LockIcon boxSize="11px" color="whiteAlpha.700" flexShrink={0} />
        <Text fontSize="13px" fontWeight="500" color="whiteAlpha.700" noOfLines={1}>
          {formattedAmount} {label.toLowerCase()}
        </Text>
      </Flex>
    </Tooltip>
  );
};

const WalletBalanceHero: React.FC<WalletBalanceHeroProps> = ({
  availableBalance,
  balanceLoading = false,
  pendingBreakdown = EMPTY_WALLET_PENDING_BREAKDOWN,
  hasWhopAccount = false,
  withdrawEnabled = false,
  connecting = false,
  onConnect,
  onOpenWallet,
  loading = false,
  payoutAction,
}) => {
  const balanceLoadingState =
    balanceLoading || (hasWhopAccount && availableBalance === undefined);

  const withdrawableBalance = hasWhopAccount ? (availableBalance ?? 0) : 0;

  const balanceText = balanceLoadingState ? null : formatWalletBalance(withdrawableBalance);

  const pendingRows = useMemo(
    () =>
      PENDING_CATEGORY_ORDER.filter((category) => pendingBreakdown[category] > 0).map((category) => ({
        category,
        amount: pendingBreakdown[category],
      })),
    [pendingBreakdown],
  );

  if (loading) {
    return (
      <WalletHeroChrome>
        <Flex align="center" justify="center" minH="142px" gap={3}>
          <Spinner size="sm" color={ff.lime} />
          <Text fontSize="sm" color={walletColors.lastPayoutText}>
            Loading wallet…
          </Text>
        </Flex>
      </WalletHeroChrome>
    );
  }

  return (
    <WalletHeroChrome>
      <Box
        cursor={onOpenWallet ? 'pointer' : 'default'}
        onClick={onOpenWallet}
        onKeyDown={
          onOpenWallet
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenWallet();
                }
              }
            : undefined
        }
        role={onOpenWallet ? 'button' : undefined}
        tabIndex={onOpenWallet ? 0 : undefined}
      >
        <Flex align="center" gap="8px" mb="9px">
          <WalletOutlineIcon color={ff.lime} />
          <Text textStyle="eyebrow" color="whiteAlpha.700" lineHeight="15px">
            Available to withdraw
          </Text>
        </Flex>

        <Box mb={pendingRows.length > 0 ? '8px' : '15px'} minH="50px">
          {balanceText ? (
            <Flex minH="50px" align="center">
              <MoneyFigure value={balanceText} label="USD" size="40px" color={ff.lime} />
            </Flex>
          ) : (
            <Flex align="center" minH="50px" gap={2}>
              <Spinner size="sm" color={ff.lime} />
              <Text fontSize="sm" color={walletColors.lastPayoutText}>
                Loading balance…
              </Text>
            </Flex>
          )}
        </Box>

        {pendingRows.length > 0 && (
          <Flex direction="column" gap="6px" mb="15px">
            {pendingRows.map(({ category, amount }) => (
              <PendingCategoryRow key={category} category={category} amount={amount} />
            ))}
          </Flex>
        )}
      </Box>

      {withdrawEnabled && payoutAction ? (
        <Box onClick={(e) => e.stopPropagation()}>{payoutAction}</Box>
      ) : hasWhopAccount ? (
        <Flex
          w="100%"
          h="48px"
          align="center"
          justify="center"
          gap="10px"
          borderRadius="button"
          bg={walletColors.withdrawButtonGradient}
          opacity={0.45}
          cursor="not-allowed"
          onClick={(e) => e.stopPropagation()}
        >
          <WalletWithdrawArrowIcon />
          <Text fontWeight="600" fontSize="15px" color={walletColors.textPrimary}>
            Withdraw
          </Text>
        </Flex>
      ) : onConnect ? (
        <Button
          variant="lime"
          w="100%"
          h="48px"
          minH="48px"
          fontSize="15px"
          boxShadow="none"
          isLoading={connecting}
          loadingText="Setting up…"
          onClick={(e) => {
            e.stopPropagation();
            onConnect();
          }}
        >
          Set up payouts
        </Button>
      ) : (
        <Flex
          w="100%"
          h="48px"
          align="center"
          justify="center"
          gap="8px"
          borderRadius="button"
          bg={walletColors.withdrawButtonGradient}
          opacity={0.45}
          cursor="not-allowed"
          onClick={(e) => e.stopPropagation()}
        >
          <LockIcon boxSize={3.5} color={walletColors.textPrimary} />
          <Text fontWeight="600" fontSize="15px" color={walletColors.textPrimary}>
            Set up payouts
          </Text>
        </Flex>
      )}
    </WalletHeroChrome>
  );
};

export default WalletBalanceHero;
